// IE Hub - Date With Clear Field Component
// Date picker with clear functionality

import React, { forwardRef } from 'react';
import {
  Box,
  Link,
  styled
} from '@mui/material';
import { isValid } from 'date-fns';
import DateField from './DateField';
import { parseDobToDate } from '@/shared/lib/utils/others';

interface ValidationRules {
  required?: boolean;
  minAge?: number;
  maxAge?: number;
  requiredMessage?: string;
}

interface DateWithClearProps {
  label?: string | React.ReactNode;
  value?: Date | string | null;
  onChange?: (event: { target: { value: Date | null } }) => void;
  validation?: ValidationRules;
  onValidate?: (isValid: boolean, error: string) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  placeholder?: string;
  sx?: Record<string, unknown>;
  // Session-replay privacy masking - when true, adds data-private (LogRocket) and data-clarity-mask (Clarity) to mask PII in recordings
  // Defaults to true for security; set to false for non-PII fields
  dataPrivate?: boolean;
  [key: string]: any;
}

// Styled clear link
const ClearLink = styled(Link)(({ theme }) => ({
  color: theme.palette.error.main,
  textDecoration: 'underline',
  cursor: 'pointer',
  fontSize: '0.875rem',
  fontWeight: 500,
  marginLeft: theme.spacing(1),
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const DateWithClearField = forwardRef<HTMLDivElement, DateWithClearProps>(
  ({
    label,
    value,
    onChange,
    validation,
    onValidate,
    error = false,
    helperText,
    disabled = false,
    placeholder = 'MM/DD/YYYY',
    sx = {},
    // LogRocket privacy masking - defaults to true
    dataPrivate = true,
    ...otherProps
  }, ref) => {
    // Parse value to Date object
    const parseValue = (val: unknown) => {
      if (!val) return null;
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        const parsed = parseDobToDate(val);
        return parsed && isValid(parsed) ? parsed : null;
      }
      return null;
    };

    const dateValue = parseValue(value);
    const hasValue = dateValue && isValid(dateValue);

    const handleClear = () => {
      const syntheticEvent = {
        target: { value: null }
      };
      onChange?.(syntheticEvent);
    };

    return (
      <Box data-testid="ui-fields-date-with-clear-field-container-action-1" ref={ref} sx={{ width: '100%', ...sx }} {...otherProps}>
        <Box data-testid="ui-fields-date-with-clear-field-container-action-2" sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
          <Box data-testid="ui-fields-date-with-clear-field-container-action-3" sx={{ flex: 1 }}>
            <DateField
              type="date"
              label={label}
              value={dateValue}
              onChange={onChange}
              validation={validation}
              onValidate={onValidate}
              error={error}
              helperText={helperText}
              disabled={disabled}
              placeholder={placeholder}
              deferValidation={true}
              dataPrivate={dataPrivate}
            />
          </Box>
          
          {hasValue && (
            <ClearLink onClick={handleClear}>
              Clear Date
            </ClearLink>
          )}
        </Box>
      </Box>
    );
  }
);

DateWithClearField.displayName = 'DateWithClearField';

export default DateWithClearField;
